import clsx from "clsx";
import React, { useContext, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { capitalize } from "lodash";

import {
  Grid,
  Typography,
  LinearProgress,
  FormControl,
  FormControlLabel,
  Checkbox,
  SvgIcon,
  Paper,
  Table,
  TableHead,
  TableSortLabel,
  TableBody,
  TableCell,
  TableRow,
  TablePagination,
  Button,
  Tooltip,
} from "@material-ui/core";
import InfoIcon from "@material-ui/icons/Info";
import { makeStyles, useTheme } from "@material-ui/core/styles";

import { FleetContext, FleetSettingsContext } from "../../context";
import { SectionGridContainer } from "../../components/Layout";
import { StringUtils } from "../../util";
import { ConfirmationModal } from "../../components/Modals";

const { formatNumber } = StringUtils;

const useStyles = makeStyles((theme) => ({
  table: {
    background: theme.palette.background.paper,
  },
  scroll: {
    width: "100%",
    overflowX: "scroll",
  },
  statusCell: {
    paddingLeft: "25px",
    width: "5px",
  },
  tableCell: {
    borderColor: theme.palette.primary.divider,
  },
  tableHead: {
    "& *": {
      background: theme.palette.background.paper,
    },
  },
  tableHeadCell: {
    borderColor: theme.palette.primary.divider,
    padding: theme.spacing(2),
  },
  tableHeadSpacing: {
    height: "100%",
    display: "flex",
    flexDirection: "column",
    alignContent: "flex-start",
    width: "auto",
  },
  editMargin: {
    marginRight: theme.spacing(1),
  },
  inactive: {
    textDecoration: "line-through",
    color: "rgb(255, 255, 255, 0.5)",
  },
  typography: {
    color: theme.palette.text.primary,
  },
  tooltipWrapper: {
    height: "15px",
    marginLeft: "5px",
  },
}));

const SettingsPasses = () => {
  const theme = useTheme();
  const classes = useStyles(theme);
  const { t } = useTranslation();
  const { activeFleet } = useContext(FleetContext);
  const {
    passOffers,
    passOffersCount,
    refreshPassOffers,
    updatePassOffer,
    orderBy,
    sortBy,
    setSortBy,
    setOrderBy,
    setPageNumber,
    setRowsPerPage,
    pageNumber,
    rowsPerPage,
    setHideDisabled,
    hideDisabled,
  } = useContext(FleetSettingsContext);
  const [loading, setLoading] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [targetPassOffer, setTargetPassOffer] = useState(null);
  const columns = [
    "disabled",
    "pass_offer_type__offer_type",
    "pass_offer_type__length",
    "pass_offer_type__max_rides",
    "pass_offer_type__free_ride_time_limit",
    "start_date",
    "end_date",
    "price",
  ];

  const hidePassOffers = (event) => {
    setHideDisabled(event.target.checked);
  };

  const handleFetchPassOffers = async (page = null) => {
    setLoading(true);
    await refreshPassOffers(activeFleet, page);
    setLoading(false);
  };

  const updatePageNumber = (val) => {
    setPageNumber(val);
    handleFetchPassOffers(val + 1);
  };

  const openModal = (row) => {
    setModalOpen(true);
    setTargetPassOffer(row);
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setTargetPassOffer(null);
  };

  const submitDeactivatePassOffer = async () => {
    setLoading(true);
    try {
      await updatePassOffer(targetPassOffer.id, { disabled: true });
      if (hideDisabled) await handleFetchPassOffers();
    } catch (e) {
      console.error(e);
    } finally {
      handleModalClose();
      setLoading(false);
    }
  };

  useEffect(() => {
    setPageNumber(0);
    handleFetchPassOffers();
  }, [activeFleet, orderBy, sortBy, rowsPerPage, hideDisabled]);

  return (
    <SectionGridContainer spaceContents>
      <Grid container spacing={2} className={classes.tableHeadSpacing}>
        <Grid item xs={12}>
          <Typography variant="h5">{t("Fleet.Settings.Passes.PassOffers")}</Typography>
        </Grid>
        <Grid item>
          <FormControl>
            <FormControlLabel
              control={<Checkbox checked={hideDisabled} onChange={hidePassOffers} />}
              label={t("Fleet.Settings.Passes.hide_passes")}
            />
          </FormControl>
        </Grid>
      </Grid>
      {loading && (
        <Grid item xs={12} style={{ padding: theme.spacing(2) }}>
          <LinearProgress color="secondary" data-testid="loading-line" />
        </Grid>
      )}
      <Paper className={classes.scroll} elevation={0}>
        <Table className={classes.table} size="small" stickyHeader data-testid="passes-table">
          <TableHead>
            <TableRow className={classes.tableHead}>
              {columns.map((key) => (
                <TableCell className={classes.tableHeadCell} key={key}>
                  <TableSortLabel
                    active={sortBy === key}
                    direction={orderBy === "asc" ? "asc" : "desc"}
                    onClick={() =>
                      sortBy === key
                        ? setOrderBy(orderBy === "asc" ? "desc" : "asc")
                        : setSortBy(key)
                    }
                  >
                    {key !== "disabled" && key !== "currency" && key !== "visible" && (
                      <Typography className={classes.typography}>
                        {t(`Fleet.Settings.Passes.Table.${key}`)}
                      </Typography>
                    )}
                  </TableSortLabel>
                </TableCell>
              ))}
              <TableCell className={classes.tableHeadCell}></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {passOffers?.map((row) => (
              <TableRow key={row.id}>
                <TableCell
                  className={
                    (clsx(classes.tableCell, row.disabled && classes.inactive), classes.statusCell)
                  }
                >
                  {!row.disabled && row.visible && (
                    <SvgIcon htmlColor={theme.palette.statusColors.available}>
                      <circle cx="18" cy="15" r="5" />
                    </SvgIcon>
                  )}
                </TableCell>
                <TableCell className={clsx(classes.tableCell, row.disabled && classes.inactive)}>
                  {capitalize(row.pass_offer_type.offer_type)}
                  {row?.pass_offer_type?.description && (
                    <Tooltip
                      className={classes.tooltipWrapper}
                      title={<div>{row?.pass_offer_type?.description}</div>}
                      placement="right"
                    >
                      <InfoIcon className={classes.infoIcon} />
                    </Tooltip>
                  )}
                </TableCell>
                {row.pass_offer_type.length === 1 ? (
                  <TableCell className={clsx(classes.tableCell, row.disabled && classes.inactive)}>
                    <Typography className={classes.typography}>
                      {t(`${row.pass_offer_type.length} day`)}
                    </Typography>
                  </TableCell>
                ) : (
                  <TableCell className={clsx(classes.tableCell, row.disabled && classes.inactive)}>
                    <Typography className={classes.typography}>
                      {t(`${row.pass_offer_type.length} days`)}
                    </Typography>
                  </TableCell>
                )}
                <TableCell className={clsx(classes.tableCell, row.disabled && classes.inactive)}>
                  {row.pass_offer_type.max_rides ?? t("Fleet.Settings.Passes.Unlimited")}
                </TableCell>
                <TableCell className={clsx(classes.tableCell, row.disabled && classes.inactive)}>
                  <Typography className={classes.typography}>
                    {t(`${row.pass_offer_type.free_ride_time_limit} minutes`)}
                  </Typography>
                </TableCell>
                <TableCell className={clsx(classes.tableCell, row.disabled && classes.inactive)}>
                  {StringUtils.convertTz(row.start_date, "UTC", "UTC", "DATE_MED")}
                </TableCell>
                <TableCell className={clsx(classes.tableCell, row.disabled && classes.inactive)}>
                  {StringUtils.convertTz(row.end_date, "UTC", "UTC", "DATE_MED")}
                </TableCell>
                <TableCell className={clsx(classes.tableCell, row.disabled && classes.inactive)}>
                  {formatNumber(row.price, row.currency)}
                </TableCell>
                {row.disabled ? (
                  <TableCell padding="none" className={classes.tableCell}></TableCell>
                ) : (
                  <TableCell padding="none" className={classes.tableCell}>
                    <Button onClick={() => openModal(row)}>
                      {t("Fleet.Settings.Passes.disabled")}
                    </Button>
                  </TableCell>
                )}
              </TableRow>
            ))}
            <TableRow>
              <TablePagination
                rowsPerPageOptions={[5, 10, 50, 100]}
                count={passOffersCount}
                rowsPerPage={rowsPerPage}
                page={pageNumber}
                backIconButtonProps={{
                  "aria-label": "Previous Page",
                }}
                nextIconButtonProps={{
                  "aria-label": "Next Page",
                }}
                onChangePage={(_, page) => updatePageNumber(page)}
                onChangeRowsPerPage={(e) => setRowsPerPage(e.target.value)}
                className={classes.tableCell}
                style={{ border: "none" }}
              />
            </TableRow>
          </TableBody>
        </Table>
      </Paper>
      <ConfirmationModal
        title={t("Fleet.Settings.Passes.disable_modal_title")}
        content={t("Fleet.Settings.Passes.disable_modal_warning")}
        submit={submitDeactivatePassOffer}
        modalOpen={modalOpen}
        handleModalClose={handleModalClose}
      />
    </SectionGridContainer>
  );
};

export default SettingsPasses;
